import DOMPurify from "dompurify";
import { ConsentDocument, SurveyDefinition, SurveyStepDefinition } from "../../../types/Survey";
import { getTextStyles, stepStyles } from "./surveyStyles";
import { DARK_SCHEME_COLORS } from "../../../helpers/surveyStyleDefaults";
import { cssColor } from "../../../helpers/surveyStyleKeys";

/** Serialize a CSS property object (kebab-case keys allowed) to an inline style string, omitting nullish values. */
function cssObjectToString(styles: Record<string, string | null | undefined>): string {
    return Object.entries(styles)
        .filter(([, v]) => v != null && v !== "")
        .map(([k, v]) => `${k}:${v}`)
        .join(";");
}

export function buildConsentReviewFrameHtml(
    consentDocument: ConsentDocument | null,
    surveyDefinition: SurveyDefinition,
    step: SurveyStepDefinition,
    hideDefaultHeader: boolean,
    includeDarkSchemeStyles: boolean,
): string {
    const defaultTitleStyle: Record<string, string> = {
        "white-space": "pre-wrap",
        "font-size": "29px",
        "text-align": "center",
        "font-weight": "normal",
        "color": "black",
    };

    // Merge survey/step overrides on top of defaults, omitting nullish values.
    const surveyTitleStyles = getTextStyles("title", surveyDefinition, step, false) as Record<string, string | null>;
    const cleanSurveyTitleStyles: Record<string, string> = Object.fromEntries(
        Object.entries(surveyTitleStyles).filter(([, v]) => v != null && v !== "") as [string, string][]
    );
    const mergedTitleStyle = { ...defaultTitleStyle, ...cleanSurveyTitleStyles };
    const titleStyleStr = cssObjectToString(mergedTitleStyle);

    let styleBlock =
        "<style type='text/css'> body { font-family: Helvetica, Arial; color:#333; padding:10px 20px 20px 20px; margin-top: 0; margin-left:0; overflow:auto; line-height:1.3 } ";
    if (hideDefaultHeader) {
        styleBlock += `header { ${titleStyleStr} }`;
    } else {
        styleBlock += "header { font-weight: bold }";
    }
    if (includeDarkSchemeStyles) {
        // We need hexes here because CSS variables don't cross into this separate document.
        const darkBackground = cssColor(stepStyles(surveyDefinition, step).darkModeBackgroundColor) || DARK_SCHEME_COLORS.cardBackground;
        styleBlock +=
            `:root.survey-scheme-dark body { background: ${darkBackground}; color: ${DARK_SCHEME_COLORS.text} } ` +
            `:root.survey-scheme-dark a { color: ${DARK_SCHEME_COLORS.accent} } ` +
            `:root.survey-scheme-dark header { color: var(--custom-text-color-dark, ${DARK_SCHEME_COLORS.text}) }`;
    }
    styleBlock += "</style>";

    let html = "";
    if (consentDocument) {
        html += `<header>${consentDocument.title}</header>`;
        for (const section of consentDocument.sections) {
            const headingText = section.formalTitle?.length ? section.formalTitle : section.title ?? "";
            html += `<p><strong>${headingText}</strong></p>`;
            if (section.htmlContent?.length) {
                html += section.htmlContent;
            } else if (section.content) {
                html += `<p>${section.content}</p>`;
            }
        }
    }

    // Sanitize, then open all links in a new tab.
    const sanitized = DOMPurify.sanitize(html);
    const wrapper = document.createElement("div");
    wrapper.innerHTML = sanitized;
    wrapper.querySelectorAll("a").forEach(a => a.setAttribute("target", "_blank"));

    return styleBlock + wrapper.innerHTML;
}

export interface ConsentHtmlStrings {
    consentPrintedNameLabel: (signatureLabel: string) => string;
    consentSignatureLabel: (signatureLabel: string) => string;
    consentDateLabel: string;
}

/**
 * Builds the full consent document HTML including the signature table, for
 * embedding in the final result.
 */
export function buildConsentHtml(opts: {
    reviewFrameHtml: string;
    signatureResult: {
        Identifier: string;
        GivenName?: string;
        FamilyName?: string;
        Title?: string;
        PDFNameLineLabel?: string;
        PDFSignatureLineLabel?: string;
        Date?: string;
    };
    consentDocument: ConsentDocument;
    signatureURL?: string | null;
    strings: ConsentHtmlStrings;
}): string {
    let html = opts.reviewFrameHtml;
    const { signatureResult, consentDocument, signatureURL, strings } = opts;

    if (signatureResult.Identifier === "NoSignature") {
        return html;
    }

    const signatureLabel = signatureResult.Title || signatureResult.Identifier;

    html +=
        `<h1 style='page-break-before:always;'>${consentDocument.signaturePageTitle ?? ""}</h1>` +
        `<div style='padding-bottom: 40px;'>${consentDocument.signaturePageContent ?? ""}</div>`;

    const hasName = !!(signatureResult.GivenName?.length && signatureResult.FamilyName?.length);
    let nameCell = "<td style='width:33%'></td>";
    let nameLabelCell = "<td></td>";
    if (hasName) {
        const label = signatureResult.PDFNameLineLabel || strings.consentPrintedNameLabel(signatureLabel);
        nameCell = `<td style='border-bottom:1px solid black;width:33%;'>${signatureResult.GivenName} ${signatureResult.FamilyName}</td>`;
        nameLabelCell = `<td style='padding-top:5px;'>${label}</td>`;
    }

    let signatureCell = "<td style='width:33%'></td>";
    let signatureLabelCell = "<td></td>";
    if (signatureURL) {
        const label = signatureResult.PDFSignatureLineLabel || strings.consentSignatureLabel(signatureLabel);
        signatureCell = `<td style='border-bottom:1px solid black;width:33%;'><img src='${signatureURL}'/></td>`;
        signatureLabelCell = `<td style='padding-top:5px;'>${label}</td>`;
    }

    html +=
        `<table style='width:100%;border-spacing: 40px 0px;'>` +
        `<tr style='vertical-align:bottom;'>` +
        nameCell +
        signatureCell +
        `<td style='border-bottom:1px solid black;width:33%;'>${signatureResult.Date ?? ""}</td>` +
        `</tr><tr>` +
        nameLabelCell +
        signatureLabelCell +
        `<td style='padding-top:5px;'>${strings.consentDateLabel}</td>` +
        `</tr></table>`;

    return html;
}